import type { FeeQuote, GasEstimate } from "../services/launcher/types";
import { formatEth } from "./format";

export interface FeeDisplayModel {
  teamExempt: boolean;
  platformFeeLabel: string;
  platformFeeValue: string;
  gasLabel: string;
  gasValue: string;
  /** Human "N units @ M gwei" detail, or null when unavailable. */
  gasUnitsDetail: string | null;
  /** True when the gas estimate is unavailable (do not show 0 ETH). */
  gasUnavailable: boolean;
  totalLabel: string;
  totalValue: string;
  teamBanner: string;
  gasReminder: string;
}

function gweiLabel(wei: bigint): string {
  // 1 gwei = 1e9 wei; show up to 3 decimals.
  const gwei = Number(wei) / 1e9;
  return `${parseFloat(gwei.toFixed(6))} gwei`;
}

/** Format a wei cost as ETH, guaranteeing a nonzero cost never renders as "0 ETH". */
function costLabel(wei: bigint): string {
  if (wei === 0n) return "0 ETH";
  const f = formatEth(wei);
  return f === "0 ETH" ? "<0.000001 ETH" : f;
}

/**
 * Build the fee display from a validated gas COST model. Only `estimatedGasCostWei`
 * is ever formatted as ETH — raw gas units are never treated as wei. The launch is
 * never described as "free". When the estimate is unavailable (null) we show a
 * message rather than 0 ETH and do not compute a total.
 */
export function buildFeeDisplay(
  quote: FeeQuote,
  gas: GasEstimate | null,
  opts: { demo?: boolean } = {}
): FeeDisplayModel {
  const gasUnavailable = gas === null;
  const demoSuffix = gas?.demonstration || opts.demo ? " (demonstration estimate)" : "";
  const gasValue = gasUnavailable ? "Gas estimate unavailable." : `${costLabel(gas.estimatedGasCostWei)}${demoSuffix}`;
  const gasUnitsDetail = gasUnavailable
    ? null
    : `${gas.gasUnits.toString()} units @ ${gweiLabel(gas.gasPriceWei)}`;
  const total = gasUnavailable ? null : quote.effectiveFee + gas.estimatedGasCostWei;

  if (quote.exempt) {
    return {
      teamExempt: true,
      platformFeeLabel: "SYNAPZ platform fee",
      platformFeeValue: "0 ETH — Team exemption",
      gasLabel: "Estimated network gas",
      gasValue,
      gasUnitsDetail,
      gasUnavailable,
      totalLabel: "Estimated total required",
      // Team pays no platform fee; the total required is the gas cost (tx value 0).
      totalValue: total === null ? "Unavailable until a gas estimate succeeds" : costLabel(total),
      teamBanner:
        "SYNAPZ Team Access Active — the platform launch fee is waived for this wallet. Robinhood Chain network gas still applies.",
      gasReminder: "Network gas still applies.",
    };
  }
  return {
    teamExempt: false,
    platformFeeLabel: "SYNAPZ platform fee",
    platformFeeValue: formatEth(quote.publicFee),
    gasLabel: "Estimated network gas",
    gasValue,
    gasUnitsDetail,
    gasUnavailable,
    totalLabel: "Estimated total required",
    totalValue: total === null ? "Unavailable until a gas estimate succeeds" : costLabel(total),
    teamBanner: "",
    gasReminder: "Network gas is additional to the platform fee.",
  };
}
